import round from 'lodash/round';
import get from 'lodash/get';
import I18n from '~/utils/i18n';
import { formatUtcFromTs } from '~/utils/format_date';
import type { ICalibrationMeasurementModel } from './model';
import { getStatus } from '../views';

// Maps the calibration record name to the on-device setting key (mirrors Batch::LUA_EVENT).
const SENSOR_KEYS = { Temperature: 'tempC', RH: 'rh', 'CO₂': 'co2', VOC: 'voc' };

const HOUR = 60 * 60 * 1000;
// A minute-cadence device reports ~60 samples/hour; treat a present hour well under that as sparse.
const SPARSE_HOUR = 45;

// A record with no fit (empty params) never reached the accuracy gate; one with params
// that is still `calibrated: false` was fitted and rejected on accuracy.
function reachedFit(measurement: ICalibrationMeasurementModel) {
  return Array.isArray(measurement.params) && measurement.params.length > 0;
}

// Reads a per-hour `buckets` list ([ts, cnt][]) and names why a device's total is low.
// Returns null when buckets are absent (pre-deploy records) or look healthy.
function bucketHint(buckets) {
  if (!Array.isArray(buckets)) return null;
  if (buckets.length === 0) return I18n.t('attributes.calibration.errors.buckets.offline');
  const sorted = [...buckets].sort((a, b) => a[0] - b[0]);
  const hasGap = sorted.some(([ts], i) => i > 0 && ts - sorted[i - 1][0] > HOUR + 1);
  if (hasGap) return I18n.t('attributes.calibration.errors.buckets.gap');
  if (sorted.every(([, cnt]) => cnt < SPARSE_HOUR)) return I18n.t('attributes.calibration.errors.buckets.sparse');
  return null;
}

// A sample gate (device or reference): counts and spread measured against what was required.
// `null`/absent gate → no issues (pre-deploy record carries no gate numbers).
function sampleGateFails(gate) {
  if (!gate) return false;
  return (gate.required_cnt != null && gate.cnt < gate.required_cnt) || (gate.required_diff != null && gate.diff < gate.required_diff);
}

function sampleGateIssues(gate, label) {
  if (!gate) return [];
  const issues: string[] = [];
  if (gate.required_cnt != null && gate.cnt < gate.required_cnt) {
    const hint = bucketHint(gate.buckets);
    const samples = I18n.t('attributes.calibration.errors.samples', { label, value: gate.cnt, required: gate.required_cnt });
    issues.push(hint ? `${samples} — ${hint}` : samples);
  }
  if (gate.required_diff != null && gate.diff < gate.required_diff) {
    issues.push(I18n.t('attributes.calibration.errors.range', { label, value: round(gate.diff, 2), required: gate.required_diff }));
  }
  return issues;
}

// The accuracy gate, straight from the record: each metric measured against its own limit,
// with the direction (min floor vs max ceiling) the backend materialized into required_metrics.
function accuracyIssues(metrics, requiredMetrics) {
  return Object.entries(requiredMetrics || {}).flatMap(([metric, limit]: [string, any]) => {
    const value = get(metrics, metric);
    if (value == null) return [];
    if (limit?.max != null && value > limit.max) {
      return [I18n.t('attributes.calibration.errors.metric', { metric, value: round(value, 2), comparator: '≤', required: limit.max })];
    }
    if (limit?.min != null && value < limit.min) {
      return [I18n.t('attributes.calibration.errors.metric', { metric, value: round(value, 2), comparator: '≥', required: limit.min })];
    }
    return [];
  });
}

export default (self: ICalibrationMeasurementModel) => {
  return {
    get status() {
      return getStatus(self);
    },
    get statusText() {
      return I18n.t(`attributes.calibration.statuses.${self.status}`);
    },
    get calibratedText() {
      return I18n.t(`attributes.calibration.${self.calibrated ? 'passed' : 'failed'}`);
    },
    get settingText() {
      // The exact string pushed to the device (mirrors Batch#update_devices): reverse ax+b -> b;a, 5 decimals.
      // Only calibrated:true results are actually pushed, so a failed fit has no device setting.
      const key = SENSOR_KEYS[self.name];
      if (!self.calibrated || !key || !reachedFit(self)) return null;
      const coeffs = [...self.params]
        .reverse()
        .map((param) => Number(param).toFixed(5))
        .join(';');
      return `${key},${coeffs}`;
    },
    get referenceDevice() {
      return `[${self.reference.serial}] ${self.reference.sensor}`;
    },
    get appliedAtText() {
      return formatUtcFromTs(self.timestamp);
    },
    get windowFromText() {
      return formatUtcFromTs(self.from_ts);
    },
    get windowToText() {
      // Fall back to the applied time for pre-deploy records that carry no window.
      return formatUtcFromTs(self.to_ts ?? self.timestamp);
    },
    get metricsText() {
      return Object.entries(get(self, 'metrics', {}))
        .map(([key, value]) => `${key}: ${round(value as number, 2)}`)
        .join(', ');
    },
    // Each metric paired with the limit the run required (from required_metrics), so the
    // acceptable range is always visible — not only next to a metric that failed.
    // `limitText`/`ok` are null when the record carries no limit for that metric (pre-deploy).
    get metricsBreakdown() {
      const required = self.required_metrics || {};
      return Object.entries(get(self, 'metrics', {})).map(([metric, raw]) => {
        const value = round(raw as number, 2);
        const limit = required[metric];
        if (limit?.max != null) {
          return { metric, value, limitText: `≤ ${limit.max}`, ok: (raw as number) <= limit.max };
        }
        if (limit?.min != null) {
          return { metric, value, limitText: `≥ ${limit.min}`, ok: (raw as number) >= limit.min };
        }
        return { metric, value, limitText: null, ok: null };
      });
    },
    // Device data-gate numbers, measured/required, for the detail card. Null on pre-deploy
    // records that carry no `samples` block.
    get dataText() {
      const s = self.samples;
      if (!s) return null;
      const hint = bucketHint(s.buckets);
      const base = I18n.t('attributes.calibration.samples_summary', {
        cnt: s.cnt,
        required_cnt: s.required_cnt,
        diff: round(s.diff, 2),
        required_diff: s.required_diff
      });
      return hint ? `${base} — ${hint}` : base;
    },
    // Reference channel health for the same run, so per-device numbers can be read in context.
    get referenceSamplesText() {
      const r = self.reference_samples;
      if (!r) return null;
      return I18n.t('attributes.calibration.samples_summary', {
        cnt: r.cnt,
        required_cnt: r.required_cnt,
        diff: round(r.diff, 2),
        required_diff: r.required_diff
      });
    },
    // The reference channel is checked first: if it starved, every device in the run failed
    // for the same reason and none is at fault — surface that rather than per-device numbers.
    get referenceAtFault() {
      return sampleGateFails(self.reference_samples);
    },
    get hasError() {
      return !self.calibrated;
    },
    // Generic reason, derived from the record's own shape so pre-deploy records (no gate
    // fields) still explain themselves: empty params → never fitted (data), else accuracy.
    get errorText() {
      if (self.calibrated) return '';
      if (this.referenceAtFault) return I18n.t('attributes.calibration.errors.reasons.reference');
      const key = reachedFit(self) ? 'accuracy' : 'no_data';
      return I18n.t(`attributes.calibration.errors.reasons.${key}`);
    },
    // Specific, measured-vs-required lines when the record carries gate numbers; empty for
    // pre-deploy records, which fall back to the generic errorText above.
    get errorDetails(): string[] {
      if (self.calibrated) return [];
      if (this.referenceAtFault) {
        return sampleGateIssues(self.reference_samples, I18n.t('attributes.calibration.errors.reference_label'));
      }
      if (reachedFit(self)) {
        return accuracyIssues(self.metrics, self.required_metrics);
      }
      return sampleGateIssues(self.samples, I18n.t('attributes.calibration.errors.device_label'));
    }
  };
};
